import React, { useEffect, useState } from "react";
import {
  getPedidos,
  updatePedido,
  deletePedido,
  getPedidoById,
  markPedidosAsRead,
} from "../../../../api/pedidos";
import DeleteIcon from "@material-ui/icons/Delete";
import Visibility from "@material-ui/icons/Visibility";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import { Button, Tooltip, CircularProgress } from "@material-ui/core";
import { buttonsStyle } from "../../../components/tables/table";
import Table from "../../../components/tables/table";
import { format } from "date-fns";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { useHistory } from "react-router-dom";

const MAIN_STATUSES = ["Pendiente", "Aceptada", "Rechazada", "Terminada"];

const normalizeStatus = (raw) => {
  if (!raw || raw === false || raw === "false" || raw === null || raw === undefined) {
    return "Pendiente";
  }
  
  const s = String(raw).toLowerCase().trim();

  // Estados en inglés
  if (s === "pending") return "Pendiente";
  if (s === "accepted" || s === "accepted by seller") return "Aceptada";
  if (s === "rejected" || s === "rejected by seller") return "Rechazada";
  if (s === "finished" || s === "completed") return "Terminada";

  // Estados en español (ya normalizados)
  if (s === "pendiente") return "Pendiente";
  if (s === "aceptada" || s === "aceptada por el vendedor") return "Aceptada";
  if (s === "rechazada" || s === "rechazada por el vendedor") return "Rechazada";
  if (s === "terminada") return "Terminada";

  // Si el estado ya está en el formato correcto (con mayúscula inicial)
  if (MAIN_STATUSES.includes(String(raw).trim())) {
    return String(raw).trim();
  }

  // Por defecto, devolver el valor original si no coincide con ningún patrón conocido
  return raw;
};

const getStatusBadgeStyle = (status) => {
  let background = "#6b7280"; // gris por defecto

  if (status === "Pendiente") background = "#fbbf24"; // amarillo
  else if (status === "Aceptada") background = "#22c55e"; // verde
  else if (status === "Rechazada") background = "#ef4444"; // rojo
  else if (status === "Terminada") background = "#3b82f6"; // azul

  return {
    display: "inline-block",
    minWidth: "110px",
    padding: "4px 10px",
    borderRadius: "999px",
    textAlign: "center",
    backgroundColor: background,
    color: "#000000",
    fontSize: "12px",
    fontWeight: 600,
  };
};

function getTotal(p) {
  const totalItems = (p.items || []).reduce((sum, item) => {
    const price = item.reloj?.price?.amount || 0;
    const qty = typeof item?.n === "number" && item.n > 0 ? item.n : (item.quantity || 1);
    return sum + price * qty;
  }, 0);
  const shippingCost = Number(p.shippingCost || 0);
  return totalItems + shippingCost;
}

function getData(pedidos) {
  return (pedidos || []).map((p) => ({
    id: p._id || p.id,
    _id: p._id || p.id,
    email: p.email,
    name: p.name,
    lastName: p.lastName,
    phone: p.phone,
    city: p.city,
    cp: p.cp,
    status: normalizeStatus(p.status),
    itemsCount: Array.isArray(p.items) ? p.items.length : 0,
    total: getTotal(p),
    createdAt: p.createdAt,
    read: p.read === true,
  }));
}

export default function PedidosPage() {
  const history = useHistory();
  const [pedidos, setPedidos] = useState([]);
  const [filteredPedidos, setFilteredPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [viewPedido, setViewPedido] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(null);
  const [bulkActionStatus, setBulkActionStatus] = useState(null);
  const [userFilter, setUserFilter] = useState("Todos");
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const loadPedidos = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getPedidos();
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const processedData = getData(data);
      setPedidos(processedData);
      setFilteredPedidos(processedData);
    } catch (e) {
      console.error("Error loading pedidos:", e);
      setError("No se pudieron cargar los pedidos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPedidos();
  }, []);

  useEffect(() => {
    // Filtrar pedidos por estado y usuario
    let filtered = pedidos;

    // Filtro por estado
    if (statusFilter === "Todos") {
      filtered = pedidos;
    } else if (statusFilter === "Otros") {
      filtered = pedidos.filter((p) => !MAIN_STATUSES.includes(p.status));
    } else {
      filtered = pedidos.filter((p) => p.status === statusFilter);
    }

    // Filtro por usuario (nombre/email)
    if (userFilter !== "Todos") {
      // Extraer nombre y email del valor del select (formato: "Nombre Apellido (email)" o "Nombre Apellido")
      const match = userFilter.match(/^(.+?)(?:\s*\((.+?)\))?$/);
      const filterName = match ? match[1].trim().toLowerCase() : userFilter.toLowerCase();
      const filterEmail = match && match[2] ? match[2].trim().toLowerCase() : null;
      
      filtered = filtered.filter((p) => {
        const fullName = `${p.name || ""} ${p.lastName || ""}`.trim();
        const email = p.email || "";
        // Comparar con el formato exacto del select
        const displayName = fullName || email ? `${fullName}${email ? ` (${email})` : ""}` : "";
        return displayName === userFilter;
      });
    }

    setFilteredPedidos(filtered);
  }, [statusFilter, userFilter, pedidos]);

  const openManage = (pedidoRow) => {
    setSelectedPedido(pedidoRow);
    setFormData({
      name: pedidoRow.name,
      lastName: pedidoRow.lastName,
      email: pedidoRow.email,
      phone: pedidoRow.phone,
      city: pedidoRow.city,
      cp: pedidoRow.cp,
      status: pedidoRow.status || "",
      statusMessage: pedidoRow.statusMessage || "",
    });
  };

  const closeManage = () => {
    setSelectedPedido(null);
    setFormData(null);
    setSaving(false);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const savePedido = async (overrideStatus) => {
    if (!selectedPedido || !formData) return;

    const payload = {
      ...formData,
      ...(overrideStatus ? { status: overrideStatus } : {}),
    };

    try {
      setSaving(true);
      await updatePedido(selectedPedido.id, payload);
      await loadPedidos();
      closeManage();
    } catch (e) {
      console.error("Error actualizando pedido:", e);
      alert("No se pudo actualizar el pedido");
      setSaving(false);
    }
  };

  const openView = async (pedidoRow) => {
    try {
      setViewLoading(true);
      const res = await getPedidoById(pedidoRow.id);
      setViewPedido(res.data || res);
      if (pedidoRow.id) {
        try {
          await updatePedido(pedidoRow.id, { read: true });
          setPedidos((prev) =>
            prev.map((p) => (p.id === pedidoRow.id || p._id === pedidoRow.id ? { ...p, read: true } : p))
          );
          setFilteredPedidos((prev) =>
            prev.map((p) => (p.id === pedidoRow.id || p._id === pedidoRow.id ? { ...p, read: true } : p))
          );
          window.dispatchEvent(new Event("refreshUnreadCounts"));
        } catch (err) {
          console.error("Error marcando pedido como leído:", err);
        }
      }
    } catch (e) {
      console.error("Error cargando detalle de pedido:", e);
      alert("No se pudo cargar el detalle del pedido");
    } finally {
      setViewLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      alertError({ customMessage: "No hay pedidos seleccionados." });
      return;
    }

    try {
      setBulkLoading(true);
      await Promise.all(selectedRows.map((row) => deletePedido(row.id)));
      alertSuccess({
        title: "Eliminados!",
        customMessage: `${selectedRows.length} pedido(s) eliminado(s) correctamente.`,
      });
      setSelectedRows([]);
      await loadPedidos();
    } catch (error) {
      alertError({
        error: error,
        customMessage: "Error al eliminar los pedidos seleccionados.",
      });
    } finally {
      setBulkLoading(false);
      setOpenConfirmDialog(null);
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedRows.length === 0) {
      alertError({ customMessage: "No hay pedidos seleccionados." });
      return;
    }

    try {
      setBulkLoading(true);
      const results = await Promise.all(
        selectedRows.map(async (row) => {
          const payload = {
            status: newStatus,
            name: row.name,
            lastName: row.lastName,
            email: row.email,
            phone: row.phone,
            city: row.city,
            cp: row.cp,
          };
          const result = await updatePedido(row.id, payload);
          return result;
        })
      );
      
      // Verificar que los estados se actualizaron correctamente
      console.log("Resultados de actualización:", results);
      
      alertSuccess({
        title: "Actualizados!",
        customMessage: `${selectedRows.length} pedido(s) actualizado(s) a "${newStatus}" correctamente.`,
      });
      setSelectedRows([]);
      await loadPedidos();
    } catch (error) {
      console.error("Error en handleBulkStatusChange:", error);
      alertError({
        error: error,
        customMessage: "Error al actualizar los pedidos seleccionados.",
      });
    } finally {
      setBulkLoading(false);
      setOpenConfirmDialog(null);
      setBulkActionStatus(null);
    }
  };

  const normalizeId = (val) => {
    if (!val) return "";
    if (typeof val === "object" && val._id) return String(val._id);
    return String(val);
  };

  const columns = [
    {
      dataField: "email",
      text: "Email",
      sort: true,
    },
    {
      dataField: "name",
      text: "Nombre",
      sort: true,
      formatter: (cell, row) => `${row.name || ""} ${row.lastName || ""}`.trim(),
    },
    {
      dataField: "phone",
      text: "Teléfono",
      sort: true,
    },
    {
      dataField: "city",
      text: "Ciudad / CP",
      sort: true,
      formatter: (cell, row) => `${row.city || ""} ${row.cp ? `(${row.cp})` : ""}`.trim(),
    },
    {
      dataField: "status",
      text: "Estado",
      sort: true,
      formatter: (cell) => (
        <span style={getStatusBadgeStyle(cell)}>{cell || "Sin estado"}</span>
      ),
    },
    {
      dataField: "itemsCount",
      text: "Items",
      sort: true,
    },
    {
      dataField: "total",
      text: "Total",
      sort: true,
      formatter: (cell) =>
        cell != null ? `${Number(cell).toLocaleString("es-ES", { style: "currency", currency: "EUR" })}` : "-",
    },
    {
      dataField: "createdAt",
      text: "Fecha",
      sort: true,
      formatter: (cell) =>
        cell ? format(new Date(cell), "yyyy-MM-dd HH:mm") : "-",
    },
    {
      dataField: "id",
      text: "Acciones",
      formatter: (cell, row) => (
        <div style={{ display: "flex", justifyContent: "center", gap: 3 }}>
            <Button
            variant="outlined"
            color="primary"
              size="small"
            startIcon={<Visibility />}
              onClick={() => openView(row)}
            style={{ 
              textTransform: "none",
              marginRight: "8px",
              minWidth: "120px"
            }}
            >
            Ver Detalles
            </Button>
          <Tooltip title="Delete">
            <Button
              style={buttonsStyle}
              size="small"
              onClick={async () => {
                if (
                  window.confirm("¿Seguro que quieres borrar este pedido?")
                ) {
                  try {
                    await deletePedido(row.id);
                    await loadPedidos();
                  } catch (e) {
                    console.error("Error borrando pedido:", e);
                    alert("No se pudo borrar el pedido.");
                  }
                }
              }}
            >
              <DeleteIcon />
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => openManage(row)}
            style={{ minWidth: "110px" }}
          >
            Gestionar
          </Button>
        </div>
      ),
      headerStyle: { width: "240px" },
    },
  ];

  return (
    <>
      <div className="card card-custom">
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
          <h3 className="card-title">Pedidos</h3>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<DoneAllIcon />}
            disabled={markingAllRead}
            onClick={async () => {
              try {
                setMarkingAllRead(true);
                await markPedidosAsRead();
                await loadPedidos();
                window.dispatchEvent(new Event("refreshUnreadCounts"));
                alertSuccess({ customMessage: "Todos los pedidos marcados como leídos." });
              } catch (err) {
                alertError({ error: err, customMessage: "Error al marcar como leídos." });
              } finally {
                setMarkingAllRead(false);
              }
            }}
          >
            {markingAllRead ? "Marcando…" : "Marcar todos como leídos"}
          </Button>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center">
              <CircularProgress />
            </div>
          ) : error ? (
            <p className="text-danger">{error}</p>
          ) : (
            <>
              <div className="form-group mb-4 d-flex flex-wrap" style={{ gap: "20px" }}>
                <div>
                  <label>Filtrar por estado</label>
                  <select
                    className="form-control"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ maxWidth: "300px" }}
                  >
                    <option value="Todos">Todos</option>
                    {MAIN_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                    <option value="Otros">Otros</option>
                  </select>
                </div>
                <div>
                  <label>Filtrar por cliente</label>
                  <select
                    className="form-control"
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    style={{ maxWidth: "300px" }}
                  >
                    <option value="Todos">Todos</option>
                    {Array.from(
                      new Set(
                        pedidos
                          .map((p) => {
                            const fullName = `${p.name || ""} ${p.lastName || ""}`.trim();
                            const email = p.email || "";
                            return fullName || email ? `${fullName}${email ? ` (${email})` : ""}` : null;
                          })
                          .filter(Boolean)
                          .sort()
                      )
                    ).map((user) => (
                      <option key={user} value={user}>
                        {user}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedRows.length > 0 && (
                <div className="mb-3 d-flex align-items-center flex-wrap" style={{ gap: "12px" }}>
                  <span className="mr-3" style={{ marginRight: "16px" }}>
                    {selectedRows.length} pedido(s) seleccionado(s)
                  </span>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setOpenConfirmDialog("delete")}
                    disabled={bulkLoading}
                    startIcon={<DeleteIcon />}
                    style={{ marginRight: "8px" }}
                  >
                    {bulkLoading ? "Eliminando..." : "Eliminar seleccionados"}
                  </Button>
                  <div style={{ width: "1px", height: "24px", backgroundColor: "#e0e0e0", margin: "0 8px" }} />
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setOpenConfirmDialog("Pendiente")}
                    disabled={bulkLoading}
                    style={{ marginRight: "8px" }}
                  >
                    Marcar como Pendiente
                  </Button>
                  <Button
                    variant="outlined"
                    style={{ borderColor: "#22c55e", color: "#22c55e", marginRight: "8px" }}
                    onClick={() => setOpenConfirmDialog("Aceptada")}
                    disabled={bulkLoading}
                  >
                    Marcar como Aceptada
                  </Button>
                  <Button
                    variant="outlined"
                    style={{ borderColor: "#ef4444", color: "#ef4444", marginRight: "8px" }}
                    onClick={() => setOpenConfirmDialog("Rechazada")}
                    disabled={bulkLoading}
                  >
                    Marcar como Rechazada
                  </Button>
                  <Button
                    variant="outlined"
                    style={{ borderColor: "#3b82f6", color: "#3b82f6" }}
                    onClick={() => setOpenConfirmDialog("Terminada")}
                    disabled={bulkLoading}
                  >
                    Marcar como Terminada
                  </Button>
                </div>
              )}

              {filteredPedidos.length === 0 ? (
                <p className="text-center">No hay pedidos todavía.</p>
              ) : (
                <Table
                  data={filteredPedidos}
                  columns={columns}
                  onSelectionChange={setSelectedRows}
                  selected={selectedRows}
                  rowClasses={(row) => (row.read === false ? "unread-row" : "")}
                />
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        title={
          openConfirmDialog === "delete"
            ? `¿Estás seguro de que quieres borrar ${selectedRows.length} pedido(s)?`
            : `¿Estás seguro de que quieres cambiar el estado de ${selectedRows.length} pedido(s) a "${openConfirmDialog}"?`
        }
        open={openConfirmDialog !== null}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => {
          if (openConfirmDialog === "delete") {
            handleBulkDelete();
          } else {
            handleBulkStatusChange(openConfirmDialog);
          }
        }}
      />

      {selectedPedido && formData && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Gestionar pedido de {selectedPedido.name} {selectedPedido.lastName}
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={closeManage}
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label>Nombre</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={formData.name}
                      onChange={handleFieldChange}
                    />
                  </div>
                  <div className="form-group col-md-6">
                    <label>Apellidos</label>
                    <input
                      type="text"
                      className="form-control"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleFieldChange}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label>Email</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={formData.email}
                      onChange={handleFieldChange}
                    />
                  </div>
                  <div className="form-group col-md-6">
                    <label>Teléfono</label>
                    <input
                      type="text"
                      className="form-control"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFieldChange}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label>Ciudad</label>
                    <input
                      type="text"
                      className="form-control"
                      name="city"
                      value={formData.city}
                      onChange={handleFieldChange}
                    />
                  </div>
                  <div className="form-group col-md-6">
                    <label>CP</label>
                    <input
                      type="text"
                      className="form-control"
                      name="cp"
                      value={formData.cp}
                      onChange={handleFieldChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Estado actual</label>
                  <select
                    className="form-control"
                    name="status"
                    value={formData.status}
                    onChange={handleFieldChange}
                  >
                    <option value="Pendiente">Pendiente</option>
                    <option value="Aceptada">Aceptada</option>
                    <option value="Rechazada">Rechazada</option>
                    <option value="Terminada">Terminada</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Mensaje al comprador</label>
                  <textarea
                    className="form-control"
                    name="statusMessage"
                    rows="4"
                    value={formData.statusMessage}
                    onChange={handleFieldChange}
                    placeholder="Escribe aquí el mensaje que verá el comprador en Mis Ofertas y en el correo."
                  />
                </div>

                <p className="text-muted">
                  Usa los botones de abajo para aceptar o rechazar el pedido. Se
                  actualizará el estado y se enviará un correo automático al comprador.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary mr-2"
                  onClick={closeManage}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-light mr-2"
                  onClick={() => savePedido()}
                  disabled={saving}
                >
                  {saving ? "Guardando..." : "Guardar cambios"}
                </button>
                <button
                  type="button"
                  className="btn btn-success mr-2"
                  onClick={() => savePedido("Aceptada")}
                  disabled={saving}
                >
                  {saving ? "Guardando..." : "Aceptar pedido"}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => savePedido("Rechazada")}
                  disabled={saving}
                >
                  {saving ? "Guardando..." : "Rechazar pedido"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewPedido && (() => {
        const sellerMap = new Map();
        (viewPedido.items || []).forEach((item) => {
          const reloj = item.reloj || {};
          const sellerUser = reloj.sellerUser;
          const legacySeller = reloj.seller;
          const seller =
            sellerUser ||
            legacySeller ||
            (typeof legacySeller === "string" ? { email: legacySeller } : null) ||
            viewPedido.user ||
            null;
          if (!seller) return;

          const email =
            sellerUser?.email ||
            (typeof legacySeller === "string" ? legacySeller : null) ||
            legacySeller?.email ||
            legacySeller?.contactEmail ||
            seller.email ||
            null;

          const key =
            email ||
            seller._id ||
            seller.fullName ||
            seller.name ||
            `seller-${Math.random()}`;

          if (sellerMap.has(key)) return;

          sellerMap.set(key, {
            name:
              sellerUser?.fullName ||
              sellerUser?.name ||
              legacySeller?.name ||
              email ||
              "Vendedor sin nombre",
            email: email || "-",
            phone:
              sellerUser?.phone ||
              sellerUser?.telefono ||
              legacySeller?.phone ||
              legacySeller?.telefono ||
              seller.phone ||
              seller.telefono ||
              "-",
          });
        });
        const sellersList = Array.from(sellerMap.values());

        return (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
          onClick={() => setViewPedido(null)}
        >
          <div
            className="modal-dialog modal-lg"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Detalle pedido {viewPedido.nOrden || viewPedido._id}
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setViewPedido(null)}
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {viewLoading ? (
                  <p>Cargando detalle...</p>
                ) : (
                  <>
                    <h6>Datos del cliente</h6>
                    <p>
                      <strong>Nombre:</strong> {viewPedido.name}{" "}
                      {viewPedido.lastName}
                      <br />
                      <strong>Email:</strong> {viewPedido.email}
                      <br />
                      <strong>Teléfono:</strong> {viewPedido.phone || "-"}
                      <br />
                      <strong>Dirección:</strong>{" "}
                      {[viewPedido.street || viewPedido.address, viewPedido.city, viewPedido.cp, viewPedido.country]
                        .filter(Boolean)
                        .join(", ") || "-"}
                    </p>

                    <h6>Información de envío</h6>
                    <p>
                      <strong>Destino:</strong> {(viewPedido.shippingDestination || "spain").toLowerCase() === "outside" ? "Internacional (por acordar)" : "España"}
                      <br />
                      <strong>Coste envío:</strong>{" "}
                      {Number(viewPedido.shippingCost || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" })}
                    </p>

                    {sellersList.length > 0 && (
                      <>
                        <h6>Vendedores</h6>
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Nombre</th>
                              <th>Email</th>
                              <th>Teléfono</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sellersList.map((seller, idx) => (
                              <tr key={`${seller.email}-${idx}`}>
                                <td>{seller.name}</td>
                                <td>{seller.email}</td>
                                <td>{seller.phone}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}

                    <h6>Items</h6>
                    <div>
                      {(viewPedido.items || []).map((item, idx) => {
                        const reloj = item.reloj || {};
                        const brand =
                          reloj.brand?.nombre ||
                          reloj.brand?.name ||
                          "";
                        const modelo =
                          reloj.specifications?.reloj_modelo?.nombre ||
                          reloj.specifications?.reloj_modelo?.name ||
                          "";
                        const name = reloj.name || "Reloj";
                        const sku = reloj.sku || "";
                        const estado =
                          reloj.specifications?.estado?.calificacion ||
                          (reloj.specifications?.estado?.usado
                            ? "Usado"
                            : "");
                        const box =
                          reloj.specifications?.contenido_de_la_entrega
                            ?.estuche === true
                            ? "Sí"
                            : "No";
                        const papers =
                          reloj.specifications?.contenido_de_la_entrega
                            ?.documento === true
                            ? "Sí"
                            : "No";
                        const vendedorNombre =
                          reloj.sellerUser?.fullName ||
                          reloj.sellerUser?.name ||
                          (typeof reloj.seller === "string"
                            ? reloj.seller
                            : reloj.seller?.name) ||
                          reloj.seller?.fullName ||
                          reloj.sellerUser?.email ||
                          reloj.seller?.email ||
                          "-";
                        const vendedorEmail =
                          reloj.sellerUser?.email ||
                          (typeof reloj.seller === "string"
                            ? reloj.seller
                            : reloj.seller?.email ||
                              reloj.seller?.contactEmail) ||
                          "-";
                        const vendedorTelefono =
                          reloj.sellerUser?.phone ||
                          reloj.sellerUser?.telefono ||
                          (typeof reloj.seller === "object"
                            ? reloj.seller?.phone || reloj.seller?.telefono
                            : "") ||
                          "";
                        const price =
                          reloj.price?.amount != null
                            ? `${reloj.price.amount} ${reloj.price.currency || "EUR"}`
                            : "-";
                        const image =
                          reloj.images && reloj.images[0]
                            ? reloj.images[0]
                            : null;
                        const productTitle = reloj.title || name;
                        const productSlug = productTitle
                          ? productTitle.toLowerCase().replace(/\s+/g, "-")
                          : "";
                        const productId = reloj.id || reloj._id;
                        const webBase =
                          process.env.WEB_URL || "https://www.turelojya.com";

                        return (
                          <div
                            key={idx}
                            style={{
                              border: "1px solid #e5e7eb",
                              borderRadius: "12px",
                              padding: "14px",
                              marginBottom: "12px",
                              display: "flex",
                              alignItems: "center",
                              gap: "16px",
                              backgroundColor: "#f9f9fb",
                            }}
                          >
                            <div>
                              {image ? (
                                <img
                                  src={image}
                                  alt={name}
                                  style={{
                                    width: "80px",
                                    height: "80px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                  }}
                                />
                              ) : (
                                "-"
                              )}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600 }}>{name}</div>
                              <div>
                                {brand} {modelo}
                                {sku && <> · SKU: {sku}</>}
                              </div>
                              <div style={{ fontSize: "12px", color: "#555" }}>
                                {estado && <>Estado: {estado} · </>}
                                Caja: {box} · Documentación: {papers}
                              </div>
                              <div style={{ marginTop: "4px", fontSize: "13px" }}>
                                <strong>Vendedor:</strong> {vendedorNombre}
                                {vendedorEmail && vendedorEmail !== "-" && (
                                  <>
                                    {" "}
                                    · <span style={{ color: "#555" }}>{vendedorEmail}</span>
                                  </>
                                )}
                                {vendedorTelefono && (
                                  <>
                                    {" "}
                                    · Tel:{" "}
                                    <span style={{ color: "#555" }}>
                                      {vendedorTelefono}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div style={{ minWidth: "100px", textAlign: "right" }}>
                              <div style={{ fontWeight: 600 }}>{price}</div>
                              {productId && (
                                <button
                                  onClick={() => history.push(`/edit-reloj/${productId}`)}
                                  className="btn btn-sm btn-outline-primary"
                                  style={{ marginTop: "6px" }}
                                >
                                  Ver reloj
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {(() => {
                      const totalItems = (viewPedido.items || []).reduce((sum, item) => {
                        const price = item.reloj?.price?.amount || 0;
                        const qty = typeof item?.n === "number" && item.n > 0 ? item.n : (item.quantity || 1);
                        return sum + price * qty;
                      }, 0);
                      const shippingCost = Number(viewPedido.shippingCost || 0);
                      const total = totalItems + shippingCost;
                      return (
                        <div style={{ marginTop: "20px", padding: "16px", backgroundColor: "#f8f9fa", borderRadius: "8px", textAlign: "right" }}>
                          {shippingCost > 0 && (
                            <>
                              <p style={{ marginBottom: "4px" }}>
                                <strong>Subtotal relojes:</strong>{" "}
                                {totalItems.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}
                              </p>
                              <p style={{ marginBottom: "4px" }}>
                                <strong>Envío:</strong>{" "}
                                {shippingCost.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}
                                {(viewPedido.shippingDestination || "").toLowerCase() === "outside" && " (por acordar)"}
                              </p>
                            </>
                          )}
                          <p style={{ fontSize: "18px", fontWeight: 600, color: "#007bff", marginBottom: 0 }}>
                            <strong>Total:</strong>{" "}
                            {total.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}
                          </p>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setViewPedido(null)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </>
  );
}


